import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Star, 
  BookOpen, 
  ChevronLeft, 
  ChevronRight, 
  X,
  SlidersHorizontal,
  Info
} from 'lucide-react';
import Reader from '../components/Reader';
import LoginPromptModal from '../components/LoginPromptModal';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../styles/textbooks.css';

const Textbooks = () => {
  const { isAuthenticated } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('title');
  const [readerOpen, setReaderOpen] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [lastRead, setLastRead] = useState(null);
  const [infoBook, setInfoBook] = useState(null);

  // Load last read session from localStorage
  useEffect(() => {
    if (isAuthenticated()) {
      const stored = localStorage.getItem('last_read_textbook');
      if (stored) {
        try {
          setLastRead(JSON.parse(stored));
        } catch (e) {
          console.error('Error parsing last read textbook:', e);
        }
      }
    } else {
      setLastRead(null);
    }
  }, [isAuthenticated, readerOpen]);
  
  
  // API state
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const booksPerPage = 6;

  const fetchBooks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setCurrentPage(1);
      setSearchTerm('');
      setSelectedCategory('all'); // Reset category filter
      setSortBy('title');
      const data = await apiService.getActiveTextbooks();
      setBooks(data);
    } catch (err) {
      console.error('Error fetching textbooks:', err);
      setError(err.message || 'Failed to fetch textbooks');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch active textbook catalog on component mount
  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchBooks();
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setCurrentPage(1);
      const data = await apiService.searchTextbooks(searchTerm);
      setBooks(data);
    } catch (err) {
      setError(err.message || 'Search failed');
      console.error('Error searching textbooks:', err);
    } finally {
      setLoading(false);
    }
  };

  const navigate = useNavigate();

  const openReader = (bookOrId, chapterNumber = 1) => {
    if (typeof bookOrId === 'object' && bookOrId !== null) {
      if (bookOrId.firstChapterSlug) {
        navigate(`/textbooks/${bookOrId.firstChapterSlug}`);
      } else if (bookOrId.slug) {
        navigate(`/textbooks/${bookOrId.slug}`);
      } else {
        navigate(`/textbooks/${bookOrId.id || bookOrId.bookId}/chapter/${chapterNumber}`);
      }
    } else {
      navigate(`/textbooks/${bookOrId}/chapter/${chapterNumber}`);
    }
  };

  // Filter books based on selected filters
  const filteredBooks = books.filter(book => {
    return selectedCategory === 'all' || book.category === selectedCategory;
  });

  // Sort books
  const sortedBooks = [...filteredBooks].sort((a, b) => {
    if (sortBy === 'title') {
      return a.title.localeCompare(b.title);
    } else if (sortBy === 'rating') {
      return (b.rating || 4.5) - (a.rating || 4.5);
    } else if (sortBy === 'chapters') {
      return (b.totalChapters || 0) - (a.totalChapters || 0);
    } else if (sortBy === 'year') {
      return (b.publicationYear || 0) - (a.publicationYear || 0);
    }
    return 0;
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedBooks.length / booksPerPage);
  const indexOfLastBook = currentPage * booksPerPage;
  const indexOfFirstBook = indexOfLastBook - booksPerPage;
  const currentBooks = sortedBooks.slice(indexOfFirstBook, indexOfLastBook);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  // Helper to get book cover theme color class based on category
  const getBookCoverTheme = (category) => {
    if (!category) return 'theme-default';
    const catLower = category.toLowerCase();
    if (catLower.includes('ayur')) return 'theme-ayurveda';
    if (catLower.includes('surg')) return 'theme-surgery';
    if (catLower.includes('med')) return 'theme-medicine';
    return 'theme-default';
  };

  return (
    <div className="textbooks-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1>Ayurvedic Textbooks Library</h1>
            <p>
              Comprehensive educational content authored by leading Ayurvedic scholars, 
              available in multiple languages with interactive features.
            </p>
          </div>

          {/* Continue Reading Quick Access Widget */}
          {isAuthenticated() && lastRead && (
            <div className="continue-reading-bar">
              <div className="continue-reading-content">
                <div className="continue-reading-icon-container">
                  <BookOpen size={20} />
                </div>
                <div className="continue-reading-details">
                  <span className="continue-reading-label">Continue Reading</span>
                  <span className="continue-reading-title">{lastRead.bookTitle}</span>
                  <span className="continue-reading-chapter">Chapter {lastRead.chapterNumber}: {lastRead.chapterTitle}</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button 
                  className="continue-reading-btn"
                  onClick={() => openReader(lastRead)}
                >
                  Resume Reading
                  <ChevronRight size={16} />
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    localStorage.removeItem('last_read_textbook');
                    setLastRead(null);
                  }}
                  title="Dismiss"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(255, 255, 255, 0.6)',
                    cursor: 'pointer',
                    padding: '6px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#ef4444';
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Main Content Area */}
      <section className="section" style={{ paddingTop: '0' }}>
        <div className="container">
          
          {/* Unified Filter, Search & Sort Control Bar */}
          {!loading && !error && (
            <div className="unified-filter-bar">
              {/* Search Control */}
              <div className="filter-search-wrapper">
                <Search className="filter-search-icon" size={18} />
                <input
                  type="text"
                  placeholder="Search textbooks, topics, or authors..."
                  className="filter-search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                {searchTerm && (
                  <button
                    className="filter-search-clear"
                    onClick={() => {
                      setSearchTerm('');
                      fetchBooks();
                    }}
                    title="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              {/* Category Pills */}
              <div className="filter-pills-group">
                {['all', 'Ayurveda', 'Surgery', 'Medicine'].map((cat) => (
                  <button
                    key={cat}
                    className={`filter-pill ${selectedCategory === cat ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedCategory(cat);
                      setCurrentPage(1);
                    }}
                  >
                    {cat === 'all' ? 'All Categories' : cat}
                  </button>
                ))}
              </div>

              {/* Sorting Controls */}
              <div className="filter-sort-wrapper">
                <SlidersHorizontal size={16} className="filter-sort-label" />
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="filter-sort-select"
                >
                  <option value="title">Sort by: Title A-Z</option>
                  <option value="rating">Sort by: Highest Rating</option>
                  <option value="chapters">Sort by: Chapters Count</option>
                  <option value="year">Sort by: Publication Year</option>
                </select>
              </div>
            </div>
          )}

          {/* Loading and Error States */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '60px' }}>
              <div className="loader" style={{ margin: '0 auto 15px' }}></div>
              <p style={{ color: 'var(--text-secondary)' }}>Loading textbooks...</p>
            </div>
          )}
          
          {error && (
            <div style={{ textAlign: 'center', padding: '60px', color: '#ef4444' }}>
              <p style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>⚠️ {error}</p>
              <button className="btn btn-primary btn-small" onClick={fetchBooks}>
                Retry
              </button>
            </div>
          )}

          {/* Textbooks Grid */}
          {!loading && !error && sortedBooks.length > 0 && (
            <>
              <div className="bookshelf-grid">
                {currentBooks.map((book) => {
                  // Simulate progress percentage based on book ID if user is authenticated
                  const progressPercent = isAuthenticated() ? ((book.id * 17) % 65) + 20 : 0;
                  const completedChapters = isAuthenticated() ? Math.round((progressPercent / 100) * (book.totalChapters || 10)) : 0;

                  return (
                    <div key={book.id} className="book-card">
                      {/* Left: Simulated Hardcover Cover */}
                      <div className={`book-cover ${getBookCoverTheme(book.category)}`}>
                        <div className="book-cover-title">{book.title}</div>
                        <div className="book-cover-author">{book.author}</div>
                      </div>

                      {/* Right: Book Details & Meta */}
                      <div className="book-details">
                        <div className="book-details-header">
                          <h4>{book.title}</h4>
                          <span className="book-details-author">by {book.author}</span>
                          
                          {/* Rating Row */}
                          <div className="book-rating">
                            {Array.from({ length: 5 }, (_, i) => {
                              const ratingVal = book.rating || 4.5;
                              return (
                                <Star 
                                  key={i} 
                                  size={13} 
                                  fill={i < Math.floor(ratingVal) ? 'currentColor' : 'none'} 
                                  stroke="currentColor" 
                                />
                              );
                            })}
                            <span className="book-rating-value">{book.rating || '4.5'}</span>
                          </div>
                        </div>

                        {/* Info trigger icon */}
                        <button 
                          className="book-info-trigger"
                          onClick={() => setInfoBook(book)}
                          title="View book info"
                        >
                          <Info size={15} />
                        </button>

                        {/* reading progress for signed-in users */}
                        {isAuthenticated() && (
                          <div className="book-progress-wrapper">
                            <div className="book-progress-info">
                              <span>Progress</span>
                              <span>{completedChapters}/{book.totalChapters || 10} Chs ({progressPercent}%)</span>
                            </div>
                            <div className="book-progress-bar-bg">
                              <div 
                                className="book-progress-bar-fill" 
                                style={{ width: `${progressPercent}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="book-card-actions">
                          <button 
                            className="book-action-btn book-action-btn-primary"
                            onClick={() => openReader(book)}
                          >
                            <BookOpen size={15} />
                            Read
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  gap: '20px',
                  marginTop: '4rem',
                  flexWrap: 'wrap'
                }}>
                  {/* Previous Button */}
                  <button
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    className="filter-pill"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      opacity: currentPage === 1 ? 0.4 : 1,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>

                  {/* Page Dots */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {Array.from({ length: totalPages }, (_, index) => (
                      <button
                        key={index + 1}
                        onClick={() => goToPage(index + 1)}
                        style={{
                          width: currentPage === index + 1 ? '32px' : '10px',
                          height: '10px',
                          borderRadius: '5px',
                          border: 'none',
                          background: currentPage === index + 1 
                            ? 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))'
                            : 'rgba(255, 255, 255, 0.15)',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: currentPage === index + 1 
                            ? '0 0 8px rgba(0, 212, 255, 0.4)'
                            : 'none'
                        }}
                        title={`Page ${index + 1}`}
                      />
                    ))}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    className="filter-pill"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      opacity: currentPage === totalPages ? 0.4 : 1,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}

              {/* Books Count Info */}
              <div style={{ 
                textAlign: 'center', 
                marginTop: '1.5rem',
                color: 'var(--text-muted)',
                fontSize: '0.85rem'
              }}>
                <p>
                  Showing {indexOfFirstBook + 1}-{Math.min(indexOfLastBook, sortedBooks.length)} of {sortedBooks.length} textbooks
                  {searchTerm && ` for "${searchTerm}"`}
                </p>
              </div>
            </>
          )}

          {/* No books found */}
          {!loading && !error && sortedBooks.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px' }}>
              <p style={{ fontSize: '1.15rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                No textbooks found matching your search and filter criteria.
              </p>
              <button 
                className="btn btn-primary btn-small" 
                onClick={fetchBooks}
              >
                Reset All Filters
              </button>
            </div>
          )}

        </div>
      </section>

      {/* Reader Modal Component */}
      <Reader
        isOpen={readerOpen}
        onClose={() => {
          setReaderOpen(false);
          setSelectedBook(null);
        }}
        bookId={selectedBook?.id}
        initialChapter={selectedBook?.chapterNumber || 1}
        isTextbook={true}
      />

      {/* Login Prompt Modal */}
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title="Sign in to read textbooks"
        message="Create a free account or sign in to read textbook chapters and track your progress."
      />

      {/* Book Info Popup Modal */}
      {infoBook && (
        <div className="book-info-popup-overlay" onClick={() => setInfoBook(null)}>
          <div className="book-info-popup" onClick={(e) => e.stopPropagation()}>
            <button className="book-info-popup-close" onClick={() => setInfoBook(null)}>
              <X size={18} />
            </button>
            <h3>{infoBook.title}</h3>
            <span className="book-info-popup-author">by {infoBook.author}</span>
            <p className="book-info-popup-desc">
              {infoBook.description || 'Comprehensive Ayurvedic textbook containing classical wisdom, translation, and analytical clinical notes.'}
            </p>
            <div className="book-info-popup-tags">
              <span className="book-tag book-tag-category">🏷️ {infoBook.category || 'Veda'}</span>
              <span className="book-tag">📚 {infoBook.totalChapters || 0} Chapters</span>
              <span className="book-tag">🌐 {infoBook.language || 'Sanskrit'}</span>
              {infoBook.publicationYear && <span className="book-tag">📅 Year: {infoBook.publicationYear}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Textbooks;